import { useEffect, useRef } from 'react';
import maplibregl, { type Map as CarteGL } from 'maplibre-gl';
import { paletteCarte } from './palette-carte.js';
import { useThemeActif } from './theme.js';

/**
 * LES COULEURS DE FOND SUIVENT LE THÈME.
 *
 * MapLibre n'accepte pas `var()` : un style ne reçoit que des littéraux. On lit
 * donc les jetons à la construction.
 *
 * ⚠️ L'effet qui construit la carte se DÉTRUIT ET SE REBÂTIT quand ses entrées
 * changent — couches, emprise, thème, arrêts sont tous dans ses dépendances
 * (voir le tableau en fin d'effet). Un commentaire antérieur décrivait ici un
 * garde `if (carte.current) return` qui n'existe plus : la revue du 12/10/2026
 * (C9) l'a retiré — un commentaire qui documente un garde-fou disparu fait
 * chercher un comportement que le code n'a pas.
 */
const jetonCarte = (n: string, repli: string): string =>
  getComputedStyle(document.documentElement).getPropertyValue(n).trim() || repli;

/**
 * LA MAQUETTE 3D DU PLAN GÉNÉRÉ (§26, §51).
 *
 * ⚠️⚠️ ELLE NE MONTRE QUE LE PLAN GÉNÉRÉ, ET C'EST LA SEULE CHOSE HONNÊTE À
 * MONTRER EN TROIS DIMENSIONS AUJOURD'HUI. Mesuré le 18/09/2026 :
 * `cad.batiment` porte **1 744 535 emprises réelles dont ZÉRO connaît sa
 * hauteur** — `hauteur_m`, `niveaux` et `annee_construction` sont vides sur la
 * totalité. Extruder ce bâti-là avec une hauteur moyenne produirait une ville
 * en volume entièrement inventée, visuellement indiscernable d'une ville
 * mesurée, et sur un instrument d'État dont les sorties sont opposables.
 *
 * Ici, chaque volume correspond à un bâtiment que le générateur a posé, avec
 * le nombre de niveaux qu'on lui a demandé : la hauteur est exacte par
 * construction. On dessine un projet, pas un territoire.
 *
 * ⚠️ FOND UNI, AUCUNE TUILE EXTERNE. La règle de l'écosystème — « 0 tuile
 * externe avec labels » — est une règle de souveraineté : faire dépendre
 * l'affichage d'un plan d'État de la disponibilité et de la lecture des
 * requêtes d'un fournisseur étranger n'est pas un détail esthétique.
 */
export interface CouchesPlan {
  voies: GeoJSON.FeatureCollection;
  ilots: GeoJSON.FeatureCollection;
  parcelles: GeoJSON.FeatureCollection;
  batiments: GeoJSON.FeatureCollection;
  equipements: GeoJSON.FeatureCollection;
}

/** La table des familles suit le thème : elle se calcule, elle ne se fige pas. */
const couleursFamille = (P: ReturnType<typeof paletteCarte>): [string, string][] => [
  ['education', P.equipement.education], ['sante', P.equipement.sante], ['commerce', P.equipement.commerce],
  ['espace_vert', P.equipement.espace_vert], ['sport', P.equipement.sport],
  ['administration', P.equipement.administration], ['securite', P.equipement.securite],
];

export function Maquette({ couches, emprise, arrets }: {
  couches: CouchesPlan;
  emprise: { ouest: number; sud: number; est: number; nord: number };
  /** Les arrêts de transport collectif de l'étape 5 — une HYPOTHÈSE de desserte, posée par-dessus le plan. */
  arrets?: GeoJSON.FeatureCollection;
}) {
  const hote = useRef<HTMLDivElement>(null);
  const carte = useRef<CarteGL | null>(null);

  const theme = useThemeActif();
  const P = paletteCarte();

  useEffect(() => {
    if (!hote.current) return;
    const c = new maplibregl.Map({
      container: hote.current,
      style: { version: 8, sources: {}, layers: [
        { id: 'fond', type: 'background', paint: { 'background-color': jetonCarte('--carte-fond', '#000000') } },
      ] },
      center: [(emprise.ouest + emprise.est) / 2, (emprise.sud + emprise.nord) / 2],
      zoom: 14, pitch: 55, bearing: -20,
      attributionControl: false,
    });
    c.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    c.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');

    c.on('load', () => {
      c.addSource('ilots', { type: 'geojson', data: couches.ilots });
      c.addSource('voies', { type: 'geojson', data: couches.voies });
      c.addSource('parcelles', { type: 'geojson', data: couches.parcelles });
      c.addSource('batiments', { type: 'geojson', data: couches.batiments });
      c.addSource('equipements', { type: 'geojson', data: couches.equipements });

      // Les îlots portent leur VOCATION : un parc et un quartier d'habitation
      // ne se distinguent pas par leur forme, seulement par leur destination.
      c.addLayer({ id: 'ilots', type: 'fill', source: 'ilots', paint: {
        'fill-color': ['match', ['get', 'vocation'],
          'espace_public', P.division[1], 'equipement', P.division[2], P.eau],
        'fill-opacity': 0.9 } });
      c.addLayer({ id: 'voies', type: 'fill', source: 'voies', paint: {
        'fill-color': ['match', ['get', 'rang'], 'primaire', P.voie[1], P.voie[2]],
        'fill-opacity': 0.95 } });
      c.addLayer({ id: 'parcelles-contour', type: 'line', source: 'parcelles', paint: {
        'line-color': jetonCarte('--carte-trait', '#2b2b2b'), 'line-width': 0.4 }, minzoom: 15 });

      // ⚠️ L'EXTRUSION LIT `hauteurM`, POSÉE PAR LE GÉNÉRATEUR. Si la propriété
      // manquait, MapLibre rendrait une hauteur nulle — une ville plate qui se
      // lirait comme un quartier de plain-pied plutôt que comme une donnée
      // absente. Le repli à 3 m est donc VISIBLE et documenté ici.
      c.addLayer({ id: 'batiments-3d', type: 'fill-extrusion', source: 'batiments', paint: {
        'fill-extrusion-color': ['interpolate', ['linear'], ['coalesce', ['get', 'hauteurM'], 3],
          3, P.volumePresume, 12, P.volumeMur, 30, P.volumeReleve, 60, P.volumeReleve],
        'fill-extrusion-height': ['coalesce', ['get', 'hauteurM'], 3],
        'fill-extrusion-base': 0,
        'fill-extrusion-opacity': 0.94 } });

      c.addLayer({ id: 'equipements-3d', type: 'fill-extrusion', source: 'equipements', paint: {
        // ⚠️ EXPRESSION LITTÉRALE, PAS UN `flat()` DE LA TABLE DES COULEURS.
        // Le typage de MapLibre exige la forme complète ; l'étaler depuis un
        // tableau compile mal et, surtout, une table vide produirait un `match`
        // sans branche — donc une couche entière rendue dans la couleur de
        // repli, sans erreur.
        'fill-extrusion-color': ['match', ['get', 'famille'],
          'education', P.equipement.education,
          'sante', P.equipement.sante,
          'commerce', P.equipement.commerce,
          'espace_vert', P.equipement.espace_vert,
          'sport', P.equipement.sport,
          'administration', P.equipement.administration,
          'securite', P.equipement.securite,
          P.equipement.autre],
        'fill-extrusion-height': 9, 'fill-extrusion-base': 0, 'fill-extrusion-opacity': 0.96 } });

      // Les arrêts de TC (étape 5) — petits volumes clairs, par-dessus tout :
      // une hypothèse de desserte doit se VOIR comme une couche à part, pas se
      // fondre dans les équipements qu'elle ne remplace pas.
      if (arrets && arrets.features.length > 0) {
        c.addSource('arrets', { type: 'geojson', data: arrets });
        c.addLayer({ id: 'arrets-3d', type: 'fill-extrusion', source: 'arrets', paint: {
          'fill-extrusion-color': P.voie[0],
          'fill-extrusion-height': 6, 'fill-extrusion-base': 0, 'fill-extrusion-opacity': 0.98 } });
      }

      const b = new maplibregl.LngLatBounds([emprise.ouest, emprise.sud], [emprise.est, emprise.nord]);
      c.fitBounds(b, { padding: 40, pitch: 55, bearing: -20, duration: 0 });

      // Survol : un volume doit pouvoir dire ce qu'il est, sinon la maquette
      // est une image et non un plan.
      const bulle = new maplibregl.Popup({ closeButton: false, closeOnClick: false });
      // ⚠️ Les arrêts aussi : « un volume doit pouvoir dire ce qu'il est » vaut
      // pour l'hypothèse comme pour le plan — surtout pour elle.
      const couchesSurvolees = ['equipements-3d', 'batiments-3d',
        ...(arrets && arrets.features.length > 0 ? ['arrets-3d'] : [])];
      for (const couche of couchesSurvolees) {
        c.on('mousemove', couche, (ev) => {
          const f = ev.features?.[0]; if (!f) return;
          c.getCanvas().style.cursor = 'pointer';
          const p = f.properties ?? {};
          bulle.setLngLat(ev.lngLat).setHTML(
            p.libelle
              ? `<strong>${p.libelle}</strong><br/>${p.famille ?? ''} · ${p.empriseM2 ?? '?'} m²`
              : `Bâtiment · ${p.niveaux ?? '?'} niveaux · ${p.hauteurM ?? '?'} m<br/>${p.plancherM2 ?? '?'} m² de plancher`
          ).addTo(c);
        });
        c.on('mouseleave', couche, () => { c.getCanvas().style.cursor = ''; bulle.remove(); });
      }
    });

    carte.current = c;
    return () => { c.remove(); carte.current = null; };
    // ⚠️ `arrets` fait partie des dépendances : une hypothèse de desserte qui
    // change sans redessiner laisserait l'ANCIENNE à l'écran — repéré par la
    // revue adversariale du 12/10/2026.
  }, [couches, emprise, theme, arrets]);

  return (
    <div className="maquette-hote">
      <div ref={hote} className="maquette-toile" />
      <div className="maquette-legende">
        <strong>Maquette du plan généré</strong>
        {couleursFamille(P).map(([f, couleur]) => (
          <span key={f}><i style={{ background: couleur }} />{f.replace('_', ' ')}</span>
        ))}
        {/* ⚠️ MÊME garde que la pose de la couche, MÊME source de couleur
            (P.voie[0]) : une légende recopiée en hex divergerait au premier
            changement de palette (revue 12/10, C17). */}
        {arrets && arrets.features.length > 0 && (
          <span><i style={{ background: P.voie[0] }} />arrêt TC (hypothèse)</span>
        )}
        <em>
          Volumes exacts : la hauteur vient des niveaux demandés. Le bâti réel du
          cadastre n'est pas représenté ici — aucune de ses 1 744 535 emprises ne
          connaît sa hauteur.
        </em>
      </div>
    </div>
  );
}
